import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMemberReports, MemberReportData } from '../../../api/memberReport';
import DexaReportsTableRow from './DexaReportsTableRow';
import './DexaReportsTable.scss';

interface DexaReportsTableProps {
  onCountUpdate: (count: number) => void;
}

const DexaReportsTable: React.FC<DexaReportsTableProps> = ({ onCountUpdate }) => {
  const [reports, setReports] = useState<MemberReportData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const location = useLocation();
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const offsetRef = useRef(0);
  const limit = 10;

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const fetchReports = useCallback(
    async (isInitial: boolean = true) => {
      try {
        if (isInitial) {
          setIsLoading(true);
          offsetRef.current = 0;
        } else {
          setIsLoadingMore(true);
        }

        const memberId = location.pathname.split('/')[2];
        if (!memberId) {
          setReports([]);
          onCountUpdate(0);
          setHasMore(false);
          return;
        }

        const currentOffset = offsetRef.current;
        const response = await getMemberReports(memberId, limit, currentOffset);
        const results = response.results || [];
        const totalCount = response.total_count || 0;

        if (isInitial) {
          setReports(results);
          onCountUpdate(totalCount);
          setHasMore(results.length === limit && results.length < totalCount);
        } else {
          setReports((prev) => {
            const newData = [...prev, ...results];
            setHasMore(results.length > 0 && results.length === limit && newData.length < totalCount);
            return newData;
          });
        }

        offsetRef.current = currentOffset + limit;
      } catch {
        setReports([]);
        onCountUpdate(0);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [location.pathname, onCountUpdate]
  );

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          fetchReports(false);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, fetchReports]
  );

  const fetchReportsRef = useRef(fetchReports);
  fetchReportsRef.current = fetchReports;

  useEffect(() => {
    fetchReportsRef.current(true);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className='scroll-container'>
        <div className='table-container dexa-reports-table'>
          <div className='table-header'>
            <div className='header-column'>Date ↓</div>
            <div className='header-column'>Name</div>
            <div className='header-column'>Type</div>
            <div className='header-column'>Status</div>
            <div className='header-column'></div>
          </div>
          <div className='table-body'>
            <div className='loading-message'>Loading DEXA reports...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='scroll-container'>
      <div className='table-container dexa-reports-table'>
        <div className='table-header'>
          <div className='header-column'>Date ↓</div>
          <div className='header-column'>Name</div>
          <div className='header-column'>Type</div>
          <div className='header-column'>Status</div>
          <div className='header-column'></div>
        </div>
        <div className='member-table-body'>
          {reports.length > 0 ? (
            reports.map((report, index) => {
              const isLast = index === reports.length - 1;
              return (
                <div key={report.id} ref={isLast ? lastElementRef : undefined}>
                  <DexaReportsTableRow report={report} onRefresh={() => fetchReports(true)} />
                </div>
              );
            })
          ) : (
            <div className='empty-message'>No DEXA reports available</div>
          )}
          {isLoadingMore && <div className='loading-message'>Loading more reports...</div>}
        </div>
      </div>
    </div>
  );
};

export default DexaReportsTable;
